import type { FC } from "react";
import { useState } from "react";
import { FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import type { UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import {
  type AllowedStatusChange,
  type RegistrationById,
  type StatusChangeRequest,
  isUnansweredMandatoryQuestionsError,
} from "../api/registrations.types";
import { DynamicForm } from "../dynamicForm";
import { StatusOptionLabel } from "../shared/RegistrationStatusDot";
import { UnansweredMandatoryQuestionsDialog } from "../shared/UnansweredMandatoryQuestionsDialog";

const shownStatus = (change: AllowedStatusChange) => (change.auditStatus === "done" ? "done" : change.status);

export const DescriptionStatusSelect: FC<{
  sharedProcessing: RegistrationById;
  updateStatusMutation: UseMutationResult<unknown, Error, StatusChangeRequest>;
}> = ({ sharedProcessing, updateStatusMutation }) => {
  const { t } = useTranslation();
  const dynamicFormState = DynamicForm.useFormState();
  const [dialogState, setDialogState] = useState<
    { open: null | "unansweredMandatoryQuestions" } | { open: "unsavedChanges"; change: AllowedStatusChange }
  >({ open: null });
  const currentStatus = sharedProcessing.status.status;
  const allowedChanges = sharedProcessing.status.allowedChanges ?? [];
  const changeTo = (status: string) => allowedChanges.find(change => shownStatus(change) === status);
  const options = [...new Set([currentStatus, ...allowedChanges.map(shownStatus)])];

  const changeStatus = (change: AllowedStatusChange) =>
    updateStatusMutation.mutate(change, {
      onError: error =>
        isUnansweredMandatoryQuestionsError(error) && setDialogState({ open: "unansweredMandatoryQuestions" }),
    });

  return (
    <>
      <Tooltip
        title={
          sharedProcessing.status.blockedReason === "noResponsible" && t("RegistrationGrid.Drawer.NoResponsibleTooltip")
        }
      >
        <FormControl>
          <Select
            id="status"
            data-testid="status-select"
            size="small"
            sx={{ minWidth: 150 }}
            disabled={allowedChanges.length === 0 || updateStatusMutation.isPending}
            value={currentStatus}
            onChange={event => {
              const change = changeTo(event.target.value);
              if (!change) return;
              if (dynamicFormState.numberOfUnsavedChanges > 0)
                return setDialogState({ open: "unsavedChanges", change });
              changeStatus(change);
            }}
          >
            {options.map(status => (
              <MenuItem
                key={status}
                value={status}
                data-testid={`status-select-option-${status}`}
                disabled={!changeTo(status)}
                sx={changeTo(status) ? {} : { display: "none" }}
              >
                <StatusOptionLabel status={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Tooltip>
      {dialogState.open === "unansweredMandatoryQuestions" && (
        <UnansweredMandatoryQuestionsDialog
          onClose={() => setDialogState({ open: null })}
          sections={
            isUnansweredMandatoryQuestionsError(updateStatusMutation.error)
              ? updateStatusMutation.error.unansweredMandatoryQuestionSections
              : []
          }
        />
      )}
      {dialogState.open === "unsavedChanges" && (
        <DynamicForm.UnsavedChangesDialog
          onCancel={() => setDialogState({ open: null })}
          onProceed={() => {
            setDialogState({ open: null });
            changeStatus(dialogState.change);
          }}
        />
      )}
    </>
  );
};
